import React from "react";
import { Box, FormControl, FormLabel, Option, Stack } from "@mui/joy";
import DarkCard from "../../common/DarkCard";
import DarkSelect from "../../common/DarkSelect";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import { SSave } from "../../../styles/StyledMUI";
import { textTokens } from "../../../styles/globalStyles";
import { useMailTemplate } from "../../../contexts/api";
import { useAlert } from "../../../contexts/app/notificationToast";
import { useConfirmationModal } from "../../../contexts/app/confirmation";
import { Permission, usePermissions } from "../../../contexts/app/permissions";
import type { MailTemplate } from "../../../api/mailTemplates/MailTemplate.typed";

const NO_MODEL = 0;

export default function UserInvitationMailPage() {
  const { listMailTemplates, getCommunicationSettings, updateCommunicationSettings } =
    useMailTemplate();
  const { hasPermission } = usePermissions();
  const { showConfirmation } = useConfirmationModal();
  const { showAlert } = useAlert();

  const canEdit =
    hasPermission(Permission.USERS_ALL_EDIT) || hasPermission(Permission.USERS_COMPANY_EDIT);

  const [templates, setTemplates] = React.useState<MailTemplate[]>([]);
  const [welcomeId, setWelcomeId] = React.useState<number>(NO_MODEL);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    void (async () => {
      const [list, settings] = await Promise.all([
        listMailTemplates("service"),
        getCommunicationSettings(),
      ]);

      if (cancelled) return;

      setTemplates(list ?? []);
      setWelcomeId(settings?.welcome_user_mail_template_id ?? NO_MODEL);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [getCommunicationSettings, listMailTemplates]);

  const onSave = React.useCallback(async () => {
    if (saving) return;

    const ok = await showConfirmation(
      "Modifier les paramètres",
      welcomeId === NO_MODEL
        ? "Aucun modèle ne sera utilisé : les invitations utilisateur ne seront plus envoyées par e-mail."
        : "Enregistrer le modèle d'invitation utilisateur ?",
    );
    if (!ok) return;

    setSaving(true);
    try {
      if (await updateCommunicationSettings({ welcome_user_mail_template_id: welcomeId })) {
        showAlert("Paramètres enregistrés", "success");
      }
    } finally {
      setSaving(false);
    }
  }, [saving, showAlert, showConfirmation, updateCommunicationSettings, welcomeId]);

  return (
    <Box sx={{ width: "100%", padding: 2, overflowY: "auto", height: "100%", maxHeight: "100%" }}>
      <Stack spacing={2} sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
        <DarkCard dense sx={{ maxWidth: "100%" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
              mb: { xs: 1.25, sm: 1.5 },
              minWidth: 0,
            }}
          >
            <DarkTypography level="title-sm" colorVariant="title" sx={{ minWidth: 0 }}>
              Invitation utilisateur
            </DarkTypography>
            {canEdit && (
              <IconButtonCTA
                aria-label="Enregistrer les paramètres"
                ctaVariant="success"
                disabled={loading || saving}
                onClick={() => void onSave()}
              >
                <SSave />
              </IconButtonCTA>
            )}
          </Box>

          <FormControl sx={{ minWidth: 0 }}>
            <FormLabel sx={{ color: textTokens.color.secondary }}>Modèle e-mail</FormLabel>
            <DarkSelect
              value={String(welcomeId)}
              disabled={!canEdit || loading || saving}
              onChange={(_e, val) => setWelcomeId(Number(val ?? NO_MODEL))}
            >
              <Option value={String(NO_MODEL)}>Aucun</Option>
              {templates.map((tpl) => (
                <Option key={tpl.id} value={String(tpl.id)}>
                  {tpl.name || `Modèle #${tpl.id}`}
                </Option>
              ))}
            </DarkSelect>
          </FormControl>
        </DarkCard>
      </Stack>
    </Box>
  );
}
